import React, { useState } from 'react';
import styled from 'styled-components';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ScrollableContainer = styled.div`
  height: 250px;
  overflow-y: auto;
  padding-right: 4px;
  margin-top: 4px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 2fr 1fr 1.2fr;
  grid-gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  
  .sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    
    &:hover {
      color: #fff;
    }
    
    &.active {
      color: rgb(0, 128, 213);
    }
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 2fr 1fr 1.2fr;
  grid-gap: 8px;
  padding: 8px 10px;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
  font-size: 12px;
  transition: background 0.15s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  
  .user {
    color: rgba(255, 255, 255, 0.45);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
  }
  
  .process {
    color: #fff;
    font-weight: 600;
    font-family: Consolas, monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .cpu {
    color: #9f7aea;
    font-weight: 700;
    font-family: Consolas, monospace;
    text-align: right;
  }
  
  .ram {
    color: #00cbd6;
    font-weight: 700;
    font-family: Consolas, monospace;
    text-align: right;
  }
`;

const ServerProcessesListWidget = ({ server, processes }) => {
    const [sortKey, setSortKey] = useState('cpu'); // default sort by CPU
    const [sortDirection, setSortDirection] = useState('desc'); // highest first

    const processesList = processes && processes.processes_list
        ? [...processes.processes_list] // copy to sort safely
        : [];

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
        } else {
            setSortKey(key);
            setSortDirection('desc');
        }
    };

    // Numerical sort comparison logic
    if (sortKey) {
        processesList.sort((a, b) => {
            let valA, valB;

            if (sortKey === 'ram' && a.ramBytes !== undefined && b.ramBytes !== undefined) {
                valA = a.ramBytes;
                valB = b.ramBytes;
            } else {
                valA = a[sortKey];
                valB = b[sortKey];

                // Parse numbers out of potentially formatted strings
                if (typeof valA === 'string') {
                    valA = parseFloat(valA.replace(/[^\d.]/g, '')) || 0;
                }
                if (typeof valB === 'string') {
                    valB = parseFloat(valB.replace(/[^\d.]/g, '')) || 0;
                }
            }

            if (valA < valB) return sortDirection === 'desc' ? 1 : -1;
            if (valA > valB) return sortDirection === 'desc' ? -1 : 1;
            return 0;
        });
    }

    const renderSortIndicator = (key) => {
        if (sortKey !== key) return null;
        return sortDirection === 'desc' ? ' ↓' : ' ↑';
    };

    return (
        <TableWrapper>
            <TableHeader>
                <div>User</div>
                <div>Process</div>
                <div 
                    className={ `sortable ${sortKey === 'cpu' ? 'active' : ''}` }
                    onClick={ () => handleSort('cpu') }
                >
                    Cpu{ renderSortIndicator('cpu') }
                </div>
                <div 
                    className={ `sortable ${sortKey === 'ram' ? 'active' : ''}` }
                    onClick={ () => handleSort('ram') }
                >
                    Ram{ renderSortIndicator('ram') }
                </div>
            </TableHeader>
            <ScrollableContainer>
                { processesList.map((process, idx) => {
                    return (
                        <TableRow key={ idx + 'zz' }>
                            <div className="user" title={ process.user }>{ process.user }</div>
                            <div className="process" title={ process.process_name }>{ process.process_name }</div>
                            <div className="cpu">{ process.cpu }</div>
                            <div className="ram">{ process.ram }</div>
                        </TableRow>
                    );
                }) }
            </ScrollableContainer>
        </TableWrapper>
    );
};

export default ServerProcessesListWidget;